import { FC, HTMLAttributes, useCallback, useMemo } from 'react';

import { Link } from 'react-router-dom';

import { ColumnDef, Row } from '@tanstack/react-table';
import { DataDisplayTable } from 'ui-kit';

import { SaleBidStructure } from '__generated__/globalTypes';

import { priceLabel, SaleOffering, STRUCTURE_LABEL } from './saleBids';
import { poolLoansPath } from './SaleOfferingsTable';
import { centralTimeParts } from './salesUtils';

export type SaleBidsTablePool = {
  offering_id: string;
  fdic_pool_number: string | null | undefined;
  offering_name: string | null | undefined;
};

export type SaleBidsTableBid = {
  bid_id: string;
  round: number;
  structure: SaleBidStructure;
  pct_e4: number | null;
  amount_cents: any;
  received_at: string;
  pools: SaleBidsTablePool[];
  bidder_name?: string;
};

type SaleBidsTableRow = SaleBidsTableBid & { __typename: string; last_updated_date: string };

const POOL_LINK_CLASS = 'font-medium text-purple-300 hover:underline light:text-purple-600';
const MUTED_TEXT = 'text-gray-400 light:text-gray-700';

// api labels a bid's pools from loan-service; saleOfferings, where the caller still has it, can
// number a pool api couldn't.
export const labelledPools = (
  bid: { offering_ids: readonly string[]; pools: readonly SaleBidsTablePool[] },
  offerings: readonly SaleOffering[],
): SaleBidsTablePool[] => {
  const labelled = new Map(bid.pools.map((pool) => [pool.offering_id, pool]));
  return bid.offering_ids.map((offeringId) => {
    const pool = labelled.get(offeringId);
    const offering = offerings.find((entry) => entry.offering_id === offeringId);
    return {
      offering_id: offeringId,
      fdic_pool_number: pool?.fdic_pool_number || offering?.fdic_pool_number,
      offering_name: pool?.offering_name || offering?.offering_name,
    };
  });
};

const poolCountLabel = (count: number) => (count === 1 ? '1 pool' : `${count} pools`);

// A bid whose pools aren't all numbered is named by its pool count: never by raw offering IDs.
const PoolsCell: FC<{ saleId: string; pools: SaleBidsTablePool[]; linkPools: boolean }> = ({
  saleId,
  pools,
  linkPools,
}) => {
  const allNumbered = pools.every((pool) => !!pool.fdic_pool_number);
  return (
    <span>
      {allNumbered
        ? pools.map((pool, index) => (
            <span key={pool.offering_id}>
              {index > 0 && ', '}
              {linkPools ? (
                <Link
                  to={poolLoansPath(saleId, pool.offering_id)}
                  className={POOL_LINK_CLASS}
                  data-testid="sale-bid-pool-link"
                >
                  {pool.fdic_pool_number}
                </Link>
              ) : (
                pool.fdic_pool_number
              )}
            </span>
          ))
        : poolCountLabel(pools.length)}
      <span className={`block text-xs ${MUTED_TEXT}`}>
        {pools.length > 1 ? `Linked · ${pools.length} pools` : pools[0]?.offering_name}
      </span>
    </span>
  );
};

const buildColumns = (
  saleId: string,
  linkPools: boolean,
  showBidder: boolean,
): ColumnDef<SaleBidsTableRow, any>[] => [
  {
    id: 'pools',
    header: 'Pool(s)',
    cell: (props) => (
      <PoolsCell saleId={saleId} pools={props.row.original.pools} linkPools={linkPools} />
    ),
    meta: { numeric: false, alignment: 'left' },
  },
  ...(showBidder
    ? [
        {
          accessorKey: 'bidder_name',
          header: 'Bidder',
          meta: { numeric: false, alignment: 'left' },
        } as ColumnDef<SaleBidsTableRow, any>,
      ]
    : []),
  {
    accessorKey: 'round',
    header: 'Round',
    meta: { numeric: true, alignment: 'left' },
  },
  {
    id: 'structure',
    header: 'Structure',
    cell: (props) => <span>{STRUCTURE_LABEL[props.row.original.structure]}</span>,
    meta: { numeric: false, alignment: 'left' },
  },
  {
    id: 'bid',
    header: 'Bid (% of BV or $)',
    cell: (props) => <span>{priceLabel(props.row.original)}</span>,
    meta: { numeric: true, alignment: 'left' },
  },
  {
    id: 'submitted',
    header: 'Submitted',
    cell: (props) => (
      <span>{centralTimeParts(props.row.original.received_at, 'numeric')?.central ?? '-'}</span>
    ),
    meta: { numeric: false, alignment: 'left' },
  },
];

export const SaleBidsTable: FC<{
  saleId: string;
  bids: SaleBidsTableBid[];
  linkPools: boolean;
  showBidder: boolean;
  tableTestId: string;
  rowTestId: string;
}> = ({ saleId, bids, linkPools, showBidder, tableTestId, rowTestId }) => {
  const columns = useMemo(
    () => buildColumns(saleId, linkPools, showBidder),
    [saleId, linkPools, showBidder],
  );
  const getRowProps = useCallback(
    (row: Row<SaleBidsTableRow>) =>
      ({
        'data-testid': rowTestId,
        'data-bid-id': row.original.bid_id,
      }) as HTMLAttributes<HTMLTableRowElement>,
    [rowTestId],
  );
  const rows: SaleBidsTableRow[] = bids.map((bid) => ({
    ...bid,
    __typename: 'SaleBid',
    last_updated_date: '',
  }));
  return (
    <div data-testid={tableTestId} className="mt-2 text-white light:text-gray-900">
      <DataDisplayTable
        data={rows}
        columns={columns}
        noDataMessage="No bids."
        getRowId={(bid) => bid.bid_id}
        getRowProps={getRowProps}
        stylingOptions={{ hoverRowColor: 'gray', supportsLightTheme: true }}
      />
    </div>
  );
};
